import {
	json,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useMatches,
	useSubmit,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { useRef } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useTheme } from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getUserImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, useUser } from './utils/user.ts'
import { Info } from 'lucide-react'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{ rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon-32x32.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'Wedding App' : 'Error | Wedding App' },
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
			() =>
				prisma.user.findUniqueOrThrow({
					select: {
						id: true,
						name: true,
						username: true,
						image: { select: { id: true } },
						roles: {
							select: {
								name: true,
								permissions: {
									select: { entity: true, action: true, access: true },
								},
							},
						},
					},
					where: { id: userId },
				}),
			{ timings, type: 'find user', desc: 'find user in root' },
		)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	const isStaging = process.env.APP_ENV === 'staging'

	return json(
		{
			user,
			isStaging,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<link rel="preconnect" href="https://fonts.googleapis.com" />
				<link rel="preconnect" href="https://fonts.gstatic.com" />
				<link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400..700&display=swap" rel="stylesheet" />
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}

				{/* Google Analytics Script */}
				<script async src="https://www.googletagmanager.com/gtag/js?id=G-YE7G6FK6H8"></script>
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `
				window.dataLayer = window.dataLayer || [];
				function gtag(){dataLayer.push(arguments);}
				gtag('js', new Date());
				gtag('config', 'G-YE7G6FK6H8');
			  `,
					}}
				/>

				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}


function App() {
	const location = useLocation();
	const data = useLoaderData<typeof loader>();
	const nonce = useNonce();
	const user = useOptionalUser();
	const theme = useTheme();
	const matches = useMatches();
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false';
	useToast(data.toast);
	const isOnHomePage = location.pathname === '/'
	const isOnGuestsPage = matches.find((m) => m.id === 'routes/guests._index')
	const isOnSeatingPage = matches.find((m) => m.id === 'routes/seating/_index')
	const isOnRsvpPage = matches.find((m) => m.id === 'routes/rsvp')
	const isOnRsvpsPage = matches.find((m) => m.id === 'routes/rsvps')
	const isOnPhotosPage = matches.find((m) => m.id === 'routes/photos')
	const isOnLoginPage = matches.find((m) => m.id === 'routes/_auth+/login')

	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<div className="flex min-h-screen h-full flex-col justify-between text-neutral-800 bg-bg-main">
				<header className="py-6 shadow-md px-10">
					<nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
						{user && (
							<div className="flex gap-10">
								<Link
									to="/"
									className={`font-semibold transition-all ${isOnHomePage
										? "text-neutral-900 underline underline-offset-8"
										: "text-neutral-800 hover:text-neutral-600"
										}`}
								>
									Home
								</Link>
								<Link
									to="/guests"
									className={`font-semibold transition-all ${isOnGuestsPage
										? "text-neutral-900 underline underline-offset-8"
										: "text-neutral-800 hover:text-neutral-600"
										}`}
								>
									Guests
								</Link>
								<Link
									to="/seating"
									className={`font-bold transition-all ${isOnSeatingPage
										? "text-neutral-900 underline underline-offset-8"
										: "text-neutral-800 hover:text-neutral-600"
										}`}
								>
									Seating
								</Link>
								<Link
									to="/rsvps"
									className={`font-bold transition-all ${isOnRsvpsPage
										? "text-neutral-900 underline underline-offset-8"
										: "text-neutral-800 hover:text-neutral-600"
										}`}
								>
									Rsvps
								</Link>
								<Link
									to="/photos"
									className={`font-bold transition-all ${isOnPhotosPage
										? "text-neutral-900 underline underline-offset-8"
										: "text-neutral-800 hover:text-neutral-600"
										}`}
								>
									Photos
								</Link>
							</div>
						)}

						<div className="flex items-center gap-10 flex-wrap">
							{user ? (
								<>
									{data.isStaging ? <Button>
										<Form action="/logout" method="POST">
											<Icon className="text-body-md" name="exit">
												<button type="submit">Guest Mode</button>
											</Icon>
										</Form>
									</Button>
										:
										<UserDropdown />
									}
								</>
							) : (
								<>
									<Link
										to="/"
										className={`font-semibold transition-all ${isOnHomePage
											? "text-neutral-900 underline underline-offset-8"
											: "text-neutral-800 hover:text-neutral-600"
											}`}
									>
										Home
									</Link>

									<Link
										to="/rsvp"
										className={`font-bold transition-all ${isOnRsvpPage
											? "text-neutral-900 underline underline-offset-8"
											: "text-neutral-800 hover:text-neutral-600"
											}`}
									>
										RSVP
									</Link>

									<Link
										to="/photos"
										className={`font-bold transition-all ${isOnPhotosPage
											? "text-neutral-900 underline underline-offset-8"
											: "text-neutral-800 hover:text-neutral-600"
											}`}
									>
										Photos
									</Link>

									<Link
										to="/login"
										className="bg-black text-white p-2 rounded"
									>
										Manage Wedding
									</Link>
								</>
							)}
						</div>
					</nav>
				</header>

				<div className="flex-1">
					{data.isStaging && (
						<div className="bg-yellow-100 text-yellow-900 border-b border-yellow-300 p-6 rounded-md">
							<div className="flex gap-4 justify-center">
								<Info className="h-[24px] w-[24px] flex-shrink-0" />
								<p className="text-md font-bold">
									{user? 'This is the demo website. To go back to the guest view area, click on the "Guest Mode" button above.' :  'This is the demo website. To enter the couples wedding manager area, click on the "Manage Wedding" button above.'}
								</p>
							</div>
						</div>
					)}


					<Outlet />
				</div>
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</Document>
	);
}


function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

function UserDropdown() {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary">
					<Link
						to={`/users/${user.username}`}
						// this is for progressive enhancement
						onClick={(e) => e.preventDefault()}
						className="flex items-center gap-2"
					>
						<span className="text-body-sm font-bold">
							{user.name ?? user.username}
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}`}>
							<Icon className="text-body-md" name="avatar">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={(event) => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
